// Create a custom interface for API response
interface ApiValueStreamResponse {
  valueStream: {
    id: string;
    label: string;
    description?: string;
    type: string;
  };
  stages: { items: any[] } | any[];
  capabilities: { items: any[] } | any[];
  processes?: { items: any[] } | any[];
  applications?: { items: any[] } | any[];
  lanes?: any[]; // Add lanes property for the lane-based API response format
}

// Enums for status and types
export enum Status {
  HEALTHY = 'healthy',
  NEEDS_IMPROVEMENT = 'needs_improvement',
  ATTENTION = 'attention'
}

export enum ProcessType {
  CORE = 'core',
  SUPPORT = 'support'
}

export enum LifecycleStage {
  PLAN = 'plan',
  BUILD = 'build',
  RUN = 'run',
  RETIRE = 'retire'
}

// Input types from API
export interface ApiValueStream {
  id: string;
  name: string;
  description: string;
  status: string;
  stages: ApiStage[];
}

export interface ApiStage {
  id: string;
  name: string;
  description: string;
  status: string;
  sequence: number;
  capabilities: ApiCapability[];
}

export interface ApiCapability {
  id: string;
  name: string;
  description: string;
  status: string;
  maturityLevel: number;
  healthScore: number;
  processes: ApiProcess[];
  applications: ApiApplication[];
}

export interface ApiProcess {
  id: string;
  name: string;
  description: string;
  status: string;
  processType: string;
  efficiencyScore: number;
}

export interface ApiApplication {
  id: string;
  name: string;
  description: string;
  status: string;
  healthScore: number;
  lifecycleStage: string;
  techStack: string[];
}

// Output types for visualization
export interface TransformedStage {
  id: string;
  name: string;
  status: string;
  nextStageId?: string;
  previousStageId?: string;
}

export interface TransformedCapability {
  id: string;
  name: string;
  status: string;
  stageId: string;
}

export interface TransformedProcess {
  id: string;
  name: string;
  status: string;
  capabilityId: string;
}

export interface TransformedApplication {
  id: string;
  name: string;
  status: string;
  capabilityIds: string[];
}

export interface TransformedValueStream {
  stages: TransformedStage[];
  capabilities: TransformedCapability[];
  processes: TransformedProcess[];
  applications: TransformedApplication[];
}

export function transformValueStreamData(data: ApiValueStreamResponse): TransformedValueStream {
  console.debug('Transforming value stream data:', {
    hasValueStream: !!data.valueStream,
    stagesType: typeof data.stages,
    hasStagesItems: data.stages && !Array.isArray(data.stages) && Array.isArray(data.stages.items),
    capabilitiesType: typeof data.capabilities,
    hasCapabilitiesItems: data.capabilities && !Array.isArray(data.capabilities) && Array.isArray(data.capabilities.items),
  });

  // Check if stages is an object with items property or an array
  const stagesArray = Array.isArray(data.stages) 
    ? data.stages 
    : (data.stages?.items || []);

  // Map out all stage IDs and names for later reference
  const stagesMap = new Map();
  stagesArray.forEach(stage => {
    stagesMap.set(stage.id, {
      id: stage.id,
      name: stage.label
    });
  });

  const stages: TransformedStage[] = stagesArray.map((stage, index) => ({
    id: stage.id,
    name: stage.label,
    status: 'active', // Default status since it's not in the API
    nextStageId: index < stagesArray.length - 1 ? stagesArray[index + 1].id : undefined,
    previousStageId: index > 0 ? stagesArray[index - 1].id : undefined,
  }));

  // Check if capabilities is an object with items property or an array
  const capabilitiesArray = Array.isArray(data.capabilities) 
    ? data.capabilities 
    : (data.capabilities?.items || []);

  // Extract capabilities and look for stage association
  const capabilities: TransformedCapability[] = [];
  
  // First approach: if capability has direct stageId property
  capabilitiesArray.forEach(capability => {
    // Clean the capability name - extract only the label without extra info
    const stageName = findStageName(capability.stageId, stagesMap);
    const cleanName = cleanCapabilityName(capability.label, stageName);
    
    capabilities.push({
      id: capability.id,
      name: cleanName,
      status: 'active',
      // Use the direct stageId if available
      stageId: capability.stageId || '',
    });
  });

  // Second approach: from API response - extract stage associations by looking at the structure
  // This handles the case where capabilities are nested under stages in the response
  if (data.stages && !Array.isArray(data.stages) && Array.isArray(data.stages.items)) {
    data.stages.items.forEach(stage => {
      // If this stage contains capabilities directly
      if (stage.data && stage.data.capabilities && Array.isArray(stage.data.capabilities)) {
        stage.data.capabilities.forEach(cap => {
          // Clean the capability name
          const stageName = stage.label || '';
          const cleanName = cleanCapabilityName(cap.label, stageName);
          
          // Find the capability in our already-extracted list
          const existingCapability = capabilities.find(c => c.id === cap.id);
          if (existingCapability) {
            // Update the stageId if we found a match
            existingCapability.stageId = stage.id;
            existingCapability.name = cleanName;
          } else {
            // Add as a new capability if not found
            capabilities.push({
              id: cap.id,
              name: cleanName,
              status: 'active',
              stageId: stage.id,
            });
          }
        });
      }
    });
  }

  // Check for lanes structure (similar to DataManagement approach)
  if (Array.isArray(data.lanes)) {
    data.lanes.forEach(lane => {
      // Some lanes might contain business capabilities
      if (lane.label === 'Business Capabilities' && Array.isArray(lane.stages)) {
        lane.stages.forEach(stageWithCapabilities => {
          // Get stage name for cleaning
          const stageName = stageWithCapabilities.label || '';
          
          // Extract capabilities if items exists
          if (Array.isArray(stageWithCapabilities.items)) {
            stageWithCapabilities.items.forEach(cap => {
              // Clean the capability name
              const cleanName = cleanCapabilityName(cap.label, stageName);
              
              // Find the capability in our already-extracted list
              const existingCapability = capabilities.find(c => c.id === cap.id);
              if (existingCapability) {
                // Update the stageId if we found a match
                existingCapability.stageId = stageWithCapabilities.id;
                existingCapability.name = cleanName;
              } else {
                // Add as a new capability if not found
                capabilities.push({
                  id: cap.id,
                  name: cleanName,
                  status: 'active',
                  stageId: stageWithCapabilities.id,
                });
              }
            });
          }
        });
      }
    });
  }

  // Process capabilities based on the data you provided - this method looks for capabilities 
  // that were sent as part of a stage's data model
  if (stagesArray.length > 0) {
    stagesArray.forEach(stage => {
      if (stage.data && Array.isArray(stage.data.capabilities)) {
        // Get stage name for cleaning
        const stageName = stage.label || '';
        
        stage.data.capabilities.forEach(cap => {
          // Clean the capability name
          const cleanName = cleanCapabilityName(cap.label, stageName);
          
          // Check if this capability already exists
          const existingCapability = capabilities.find(c => c.id === cap.id);
          
          if (existingCapability) {
            // Update stageId since now we know which stage it belongs to
            existingCapability.stageId = stage.id;
            existingCapability.name = cleanName;
          } else {
            // Add as a new capability
            capabilities.push({
              id: cap.id,
              name: cleanName,
              status: 'active',
              stageId: stage.id,
            });
          }
        });
      }
    });
  }

  // Check if processes are available in the API response
  // The API might not include processes, so handle that case
  const processesArray = data.processes ? (
    Array.isArray(data.processes) 
      ? data.processes 
      : (data.processes?.items || [])
  ) : [];
    
  // Safely map processes if any exist
  const processes: TransformedProcess[] = processesArray.length > 0 
    ? processesArray.map(process => ({
        id: process.id,
        name: process.label || process.name,
        status: 'active', // Default status
        capabilityId: process.capabilityId,
      }))
    : [];

  // Check if applications are available in the API response
  // The API might not include applications, so handle that case
  const applicationsArray = data.applications ? (
    Array.isArray(data.applications) 
      ? data.applications 
      : (data.applications?.items || [])
  ) : [];
    
  // Safely map applications if any exist
  const applications: TransformedApplication[] = applicationsArray.length > 0
    ? applicationsArray.map(app => ({
        id: app.id,
        name: app.label || app.name,
        status: 'active', // Default status
        capabilityIds: app.capabilityIds || [app.capabilityId].filter(Boolean),
      }))
    : [];

  console.debug('Transformed value stream:', {
    stages: stages.length,
    capabilities: capabilities.length,
    processes: processes.length,
    applications: applications.length
  });

  return {
    stages,
    capabilities,
    processes,
    applications
  };
}

// Helper function to map API status to enum
export function mapStatus(apiStatus: string): Status {
  const statusMap: Record<string, Status> = {
    'HEALTHY': Status.HEALTHY,
    'NEEDS_IMPROVEMENT': Status.NEEDS_IMPROVEMENT,
    'ATTENTION': Status.ATTENTION
  };
  return statusMap[apiStatus] || Status.ATTENTION;
}

// Helper function to map API process type to enum
export function mapProcessType(apiType: string): ProcessType {
  const typeMap: Record<string, ProcessType> = {
    'CORE': ProcessType.CORE,
    'SUPPORT': ProcessType.SUPPORT
  };
  return typeMap[apiType] || ProcessType.SUPPORT;
}

// Helper function to map API lifecycle stage to enum
export function mapLifecycleStage(apiStage: string): LifecycleStage {
  const stageMap: Record<string, LifecycleStage> = {
    'PLAN': LifecycleStage.PLAN,
    'BUILD': LifecycleStage.BUILD,
    'RUN': LifecycleStage.RUN,
    'RETIRE': LifecycleStage.RETIRE
  };
  return stageMap[apiStage] || LifecycleStage.RUN;
}

/**
 * Find the stage name from a stageId using the stages map
 */
function findStageName(stageId: string, stagesMap: Map<string, { id: string, name: string }>): string {
  if (!stageId) return '';
  const stage = stagesMap.get(stageId);
  return stage ? stage.name : '';
}

/**
 * Clean up a capability name by removing stage name and technical suffixes
 */
function cleanCapabilityName(capabilityLabel: string, stageName: string): string {
  if (!capabilityLabel) return '';
  
  // First split by hyphen and take the first part to remove technical IDs
  let cleanName = capabilityLabel.split('-')[0].trim();
  
  // If the capability name contains the stage name, remove it
  if (stageName && cleanName.includes(stageName)) {
    cleanName = cleanName.replace(stageName, '').trim();
  }
  
  // Remove any leading/trailing punctuation
  cleanName = cleanName.replace(/^[^\w]+|[^\w]+$/g, '').trim();
  
  // Use the original label if cleaning resulted in an empty string
  return cleanName || capabilityLabel;
} 